import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

import {
  EventsHistoryApiPicker,
  EventsHistoryQueryParams,
  UsersApi,
  ViError,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { EventsStore, InstallationDetailsStore } from '@store';

import { httpHeaders } from '@vi-iot/client/client.service';
import { EventsActions } from './events.actions';
import { END_OF_LIST, EventsData } from './events.model';

import { downloadFile } from '@common';

const PAGE_LIMIT = 50;

@Injectable()
export class EventsEffects {
  // ?INFO: This UUID is used to identify the Estrella support user in the system. Created by Viessmann Employee.

  readonly EstrellaSupportUUID = 'estrella-support';

  loadEvents$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventsActions.loadEvents),
      concatLatestFrom(() => [
        this.eventsStore.state$,
        this.installationDetailsStore.gatewaySerial$,
        this.installationDetailsStore.getInstallationId$,
      ]),
      switchMap(([_action, state, gatewaySerials, installationId]) => {
        const params: EventsHistoryQueryParams = { gatewaySerials, limit: PAGE_LIMIT };

        if (state.cursor) {
          params.cursor = state.cursor;
        }

        if (state.filters) {
          params.eventType = state.filters;
        }

        if (state.dateFilter) {
          params.timestamp = state.dateFilter;
        }

        return this.eventHistoryApiPicker
          .instance()
          .getEvents(installationId.toString(), params)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(EventsActions.Type.RESET_EVENTS))),
            map((response: EventsData) => {
              return EventsActions.loadEventsSuccess({
                eventsList: response.data,
                cursor: response.cursor.next || END_OF_LIST,
              });
            }),
            catchError(() => {
              this.toast.error({
                message: this.translation.instant(
                  '+INSTALLATIONS.INSTALLATION.EVENT_LOG.LOADING_ERROR'
                ),
              });

              return of(EventsActions.loadEventsError());
            })
          );
      })
    );
  });

  loadFilteredEvents$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventsActions.updateFilters),
      map(() => EventsActions.loadEvents())
    );
  });

  loadUserDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventsActions.loadUserDetails),
      filter((action) => action.uuid !== this.EstrellaSupportUUID),
      mergeMap((action) =>
        this.usersApi.getUser(action.uuid, httpHeaders).pipe(
          takeUntil(this.actions$.pipe(ofType(EventsActions.Type.RESET_EVENTS))),
          map((data) =>
            EventsActions.loadUserDetailsSuccess({
              userDetails: {
                username: data.name.firstName + ' ' + data.name.familyName,
                uuid: data.id,
                userRole: data.iotRole,
              },
            })
          ),
          catchError(() => {
            this.toast.error({
              message: this.translation.instant(
                '+INSTALLATIONS.INSTALLATION.EVENT_LOG.LOADING_ERROR'
              ),
            });

            return of(EventsActions.loadUserDetailsError());
          })
        )
      )
    );
  });

  triggerEventLogFileGeneration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventsActions.triggerEventLogFileGeneration.attempt),
      concatLatestFrom(() => this.installationDetailsStore.getInstallationId$),
      switchMap(([_action, installationId]) =>
        this.eventHistoryApiPicker
          .instance()
          .triggerEventLogFileGeneration(installationId)
          .pipe(
            map((data) =>
              EventsActions.triggerEventLogFileGeneration.success({
                processId: data.data,
                installationId,
              })
            ),
            catchError((error) => {
              this.toast.error({
                message: this.translation.instant(
                  '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DOWNLOAD.UNABBLE_TO_GENERATE'
                ),
              });

              return of(EventsActions.triggerEventLogFileGeneration.error({ error }));
            })
          )
      )
    );
  });

  startEventLogDownloadPolling$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventsActions.triggerEventLogFileGeneration.success),
      map(({ processId, installationId }) =>
        EventsActions.downloadEventLogFile.attempt({ processId, installationId })
      )
    );
  });

  downloadEventLogFile$ = createEffect(() => {
    const delayBetweenRequests = 2000;
    const requestThreshold = 10;
    const timeoutError = { message: 'Viguide: No data before timeout' } as ViError;
    const cleanupAction$ = this.actions$.pipe(ofType(EventsActions.downloadEventLogFile.attempt));

    return this.actions$.pipe(
      ofType(EventsActions.downloadEventLogFile.attempt),
      switchMap(({ installationId, processId }) =>
        timer(500, delayBetweenRequests).pipe(
          mergeMap(() =>
            this.eventHistoryApiPicker.instance().downloadEventLogFile(installationId, processId)
          ),
          concatMap(({ data }, index) => {
            if (index === requestThreshold && !data) {
              return throwError(() => timeoutError);
            }

            return of(data);
          }),
          filter((url) => !!url),
          take(1),
          takeUntil(cleanupAction$)
        )
      ),
      map((url) => {
        this.toast.success({
          message: this.translation.instant(
            '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DOWNLOAD.DOWNLOAD_SUCCESSFUL'
          ),
        });
        downloadFile(url);

        return EventsActions.downloadEventLogFile.success();
      }),
      catchError((error: ViError) => {
        if (error.message === timeoutError.message) {
          // timeout
          this.toast.error({
            message: this.translation.instant(
              '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DOWNLOAD.UNABBLE_TO_GENERATE'
            ),
          });
        } else {
          this.toast.error({
            message: this.translation.instant(
              '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DOWNLOAD.ERROR_DOWNLOAD'
            ),
          });
        }

        return of(EventsActions.downloadEventLogFile.error({ error }));
      })
    );
  });

  constructor(
    private eventsStore: EventsStore,
    private eventHistoryApiPicker: EventsHistoryApiPicker,
    private usersApi: UsersApi,
    private actions$: Actions,
    private toast: ViToastService,
    private translation: TranslateService,
    private installationDetailsStore: InstallationDetailsStore
  ) {}
}
